import { Component, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { WidgetService } from 'src/app/services/widget.service';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from 'src/app/services/config.service';
import { interval, Subscription, lastValueFrom } from 'rxjs';

@Component({
    selector: 'app-license-widget',
    templateUrl: './license-widget.component.html',
    styleUrls: ['../base-widget/base-widget.component.scss', './license-widget.component.scss'],
    standalone: false
})
export class LicenseWidgetComponent extends BaseWidgetComponent implements OnInit, OnDestroy {
  public title: string = "License Status";
  public licenseStatus: 'valid' | 'trial' | 'expired' = null;
  public daysRemaining: number = 0;
  public message: string = '';
  public loading: boolean = true;
  private refreshSubscription: Subscription;

  // License activation form
  public licenseeInput: string = '';
  public signatureInput: string = '';
  public activating: boolean = false;
  public activationError: string = '';

  // Configuration - update with your actual license purchase URL
  purchaseUrl: string = 'https://lit.ai/purchase-license';

  constructor(
    private _widgetService: WidgetService,
    private _elRef: ElementRef,
    private http: HttpClient
  ) {
    super();
    (<any>window).licenseWidget = this;
  }

  ngOnInit(): void {
    // Skip license checks in local mode - it's free forever
    if (ConfigService.localMode) {
      this.loading = false;
      this.licenseStatus = 'valid';
      this.message = 'Local mode - no license required';
      return;
    }

    this.fetchLicenseStatus();

    // Set up automatic refresh every hour
    this.refreshSubscription = interval(3600000).subscribe(() => {
      this.fetchLicenseStatus();
    });
  }
  
  ngOnDestroy(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
  }

  async fetchLicenseStatus() {
    this.loading = true;
    try {
      const response: any = await lastValueFrom(this.http.get(`${ConfigService.apiUrl}/init/trial`));

      if (response.licensed) {
        // Licensed user
        this.licenseStatus = 'valid';
        this.message = 'Your license is valid. Thank you for using LIT Platform!';
        this.daysRemaining = 0;
      } else if (response.active) {
        // Trial is active
        this.licenseStatus = 'trial';
        this.daysRemaining = response.days_remaining;
        this.message = `Your trial period will expire in ${this.daysRemaining} day${this.daysRemaining !== 1 ? 's' : ''}.`;
      } else {
        // Trial expired
        this.licenseStatus = 'expired';
        this.message = 'Your trial period has expired.';
        this.daysRemaining = 0;
      }
    } catch (error) {
      console.error('Error fetching license status:', error);
      // On error, default to warning
      this.licenseStatus = 'expired';
      this.message = 'Unable to verify license status. Please contact support.';
      this.daysRemaining = 0;
    } finally {
      this.loading = false;
    }
  }
  
  goToPurchasePage() {
    // Open the purchase URL in a new tab
    window.open(this.purchaseUrl, '_blank');
  }

  async activateLicense() {
    if (!this.licenseeInput || !this.signatureInput) {
      return;
    }

    this.activating = true;
    this.activationError = '';

    try {
      const response: any = await lastValueFrom(
        this.http.post(`${ConfigService.apiUrl}/v1/license/activate`, {
          licensee: this.licenseeInput,
          signature: this.signatureInput
        })
      );

      if (response.success) {
        // Refresh status to show the new licensed state
        await this.fetchLicenseStatus();
        // Clear the form
        this.licenseeInput = '';
        this.signatureInput = '';
      }
    } catch (error: any) {
      console.error('Error activating license:', error);
      this.activationError = error.error?.message || 'Failed to activate license. Please check your credentials.';
    } finally {
      this.activating = false;
    }
  }
}
